import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram } from 'lucide-react';
import SlimGridMotion from '../SlimGridMotion';
import Reveal from '../home/Reveal';
import GreenButton from '../home/GreenButton';
import SectionHeading from '../home/SectionHeading';
import ParallaxImage from '../home/ParallaxImage';
import HowItWorks from '../home/HowItWorks';
import OptionsAccordion from './OptionsAccordion';
import UspStrip from './UspStrip';
import QuestionsAnswered from './QuestionsAnswered';
import { useContactModal } from '../../contexts/ContactModalContext';
import type { ServicePageData, ServiceStep } from '../../data/services/types';

const defaultSteps: ServiceStep[] = [
  {
    title: 'Get a free quote',
    text: 'Call, email or message us with what you need. Choose a standard size or arrange a free site visit and we will measure up.',
  },
  {
    title: 'Design stage',
    text: 'Already have artwork? Send it over for a proof. Need help? Send your logo and our design team will mock up how it could look.',
  },
  {
    title: 'Approve your order',
    text: 'Sign off the visual and we go into production in our Kidderminster unit. Most jobs complete within 5 to 10 working days.',
  },
  {
    title: 'Order up',
    text: 'Collect from our shop, have it delivered from £10, or book our installation team to fit it for you.',
  },
];

interface ServicePageProps {
  data: ServicePageData;
}

const ServicePage: React.FC<ServicePageProps> = ({ data }) => {
  const { openContactModal } = useContactModal();
  const steps = data.steps ?? defaultSteps;
  const questions = [
    ...(data.faqs ?? []).map((f) => ({ q: f.q, a: f.a, bullets: undefined as string[] | undefined })),
    ...data.info.map((b) => ({ q: b.title, a: b.text, bullets: b.bullets })),
  ];

  const scrollToOptions = () => {
    document.querySelector('#options')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-black">
      {/* Hero */}
      <section className="relative overflow-hidden bg-black text-white min-h-[70svh] lg:h-[calc(60svh_-_var(--header-h,0px))] lg:min-h-[540px] flex items-center">
        {data.heroImages ? (
          <div className="absolute inset-0 isolate">
            <SlimGridMotion items={data.heroImages} gradientColor="transparent" />
          </div>
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${data.heroImage})` }}
          />
        )}
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/70 via-transparent to-black/40" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-5 mb-5">
              <span className="h-px w-12 bg-[#78BE20]" />
              <Link
                to={data.eyebrowTo ?? '/services/all-signage'}
                className="font-poppins text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase hover:text-[#78BE20] transition-colors"
              >
                {data.eyebrow}
              </Link>
            </div>
            <h1 className="font-montserrat font-extrabold leading-[1.02] tracking-[-0.03em] text-[clamp(34px,4vw,60px)]">
              {data.title} <span className="text-[#78BE20]">{data.titleAccent}</span>
            </h1>
            <p className="font-poppins text-base md:text-lg leading-7 text-white/80 mt-6 max-w-xl">{data.intro}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {data.heroPrimaryButtonText ? (
                <GreenButton onClick={scrollToOptions}>{data.heroPrimaryButtonText}</GreenButton>
              ) : (
                <GreenButton onClick={openContactModal}>Get a free quote</GreenButton>
              )}
              {data.heroSecondaryButtonText ? (
                <GreenButton onClick={openContactModal} variant="outline" className="text-white">
                  {data.heroSecondaryButtonText}
                </GreenButton>
              ) : (
                <GreenButton onClick={scrollToOptions} variant="outline" className="text-white">
                  {data.optionsHeading}
                </GreenButton>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Perfect for */}
      {!data.hidePerfectFor && data.useCases && data.useCases.length > 0 && (
        <section className="py-16 md:py-20 px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20 items-start">
            <Reveal>
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
                Perfect for
              </h2>
              <p className="font-poppins text-base leading-7 text-black/70 mt-4">
                Made to your exact specification and finished to a high standard. Here is where it earns its keep.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7">
                {data.useCases.map((u, i) => (
                  <li key={u.title} className="flex gap-4">
                    <span className="font-montserrat font-extrabold text-[#78BE20] text-lg leading-none pt-1">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <p className="font-montserrat font-bold text-[15px]">{u.title}</p>
                      <p className="font-poppins text-sm leading-6 text-black/60 mt-1">{u.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      {/* Options accordion */}
      <section id="options" className="py-16 md:py-24 px-6 border-t border-black/10">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-12 lg:mb-16">
            <SectionHeading title={data.optionsHeading} subtitle={data.optionsIntro} tone="green" underline />
          </Reveal>
          <OptionsAccordion items={data.options} />
        </div>
      </section>

      {/* Optional USP bar */}
      {data.uspItems && data.uspItems.length > 0 && <UspStrip items={data.uspItems} />}

      {/* Customisation */}
      {!data.hideCustomisation && data.customisation && data.customisation.length > 0 && (
        <section className="py-16 md:py-20 px-6 border-t border-black/10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20 items-start">
            <Reveal>
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
                Types of customisation
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {data.customisation.map((c, i) => (
                <Reveal key={c.title} delay={i * 80}>
                  <div className="border-l-2 border-[#78BE20] pl-5">
                    <h3 className="font-montserrat font-bold text-lg">{c.title}</h3>
                    <p className="font-poppins text-sm leading-6 text-black/70 mt-2">{c.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {data.gallery.length > 0 && (
        <section className="py-16 md:py-20 px-6 bg-neutral-50 border-t border-black/10">
          <div className="max-w-6xl mx-auto">
            <Reveal className="flex items-end justify-between gap-6 mb-10">
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
                Recent work
              </h2>
              <a
                href="https://www.instagram.com/outpostcustom/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-2 font-poppins text-[11px] font-semibold tracking-[0.12em] uppercase text-black/60 hover:text-[#78BE20] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={2} />
                More on Instagram
              </a>
            </Reveal>
            <div className="columns-2 md:columns-3 gap-4 [&>*]:mb-4">
              {data.gallery.map((src, i) => (
                <Reveal key={src} delay={(i % 3) * 80} className="break-inside-avoid overflow-hidden bg-neutral-200">
                  <img
                    src={src}
                    alt={`${data.title} ${data.titleAccent} example ${i + 1}`}
                    className="w-full h-auto block transition-transform duration-700 hover:scale-[1.03]"
                    loading="lazy"
                  />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      <HowItWorks steps={steps} tone={data.howItWorksTone} />

      {/* FAQ */}
      <QuestionsAnswered questions={questions} dark={data.darkFaq ?? false} />

      {/* CTA band */}
      {data.greenCta ? (
        <section className="w-full py-20 md:py-28 px-6 bg-[#78BE20] text-white">
          <div className="max-w-[900px] mx-auto text-center">
            <Reveal>
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(36px,5vw,64px)] mb-4">
                Ready to get started?
              </h2>
              <p className="font-poppins text-white/85 text-base md:text-lg mb-10 max-w-xl mx-auto">
                {data.cta?.text || 'Talk to our team today and we will help you find the right solution for your project.'}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={openContactModal}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-black hover:text-white transition-colors"
                >
                  Get in touch
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <a
                  href="tel:01562227117"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-white text-white font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-white hover:text-black transition-colors"
                >
                  Call 01562 227 117
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      ) : (
        <ParallaxImage
          src={data.cta.image}
          overlay="linear-gradient(to right, rgba(0,0,0,0.85), rgba(0,0,0,0.45))"
          className="min-h-[420px] md:min-h-[520px] flex items-center text-white px-6"
        >
          <div className="max-w-6xl mx-auto w-full py-20">
            <Reveal>
              <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(32px,4.5vw,64px)] max-w-3xl">
                {data.cta.title}
              </h2>
              <p className="font-poppins text-base md:text-lg text-white/80 mt-5 max-w-xl">{data.cta.text}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <GreenButton onClick={openContactModal}>Get a free quote</GreenButton>
                <a
                  href="tel:01562227117"
                  className="inline-flex items-center font-poppins font-semibold text-[11px] tracking-[0.12em] uppercase px-6 py-3 border border-white/70 hover:bg-white hover:text-black transition-colors"
                >
                  Call 01562 227 117
                </a>
              </div>
            </Reveal>
          </div>
        </ParallaxImage>
      )}
    </div>
  );
};

export default ServicePage;
